import type { ChatPlayerCompiledChat } from "./chat-player-types";

import { CHAT_PLAYER_IDLE_TIME_MS, getNextChatPlayerUpdateTimeMs } from "./chat-player-playback";
import { clampMs } from "./chat-player-timing";

export { CHAT_PLAYER_IDLE_TIME_MS } from "./chat-player-playback";

type AutoPlaybackFrameInput = {
  currentFrame: ChatPlayerAutoPlaybackFrame;
  deltaMs: number;
  durationMs: number;
  loop: boolean;
  loopResetMs: number;
};

export type ChatPlayerAutoPlaybackFrame = {
  timeMs: number;
  playbackKey: number;
  resetRemainingMs: number;
};

export function createAutoPlaybackFrame(timeMs: number, playbackKey = 0) {
  return {
    timeMs,
    playbackKey,
    resetRemainingMs: 0,
  } satisfies ChatPlayerAutoPlaybackFrame;
}

export function getInitialAutoPlaybackTimeMs({
  durationMs,
  initialTimeMs,
}: {
  durationMs: number;
  initialTimeMs: number;
}) {
  return clampMs(initialTimeMs, 0, durationMs);
}

export function getInitialMediaPlaybackTimeMs({
  currentTimeMs,
  durationMs,
  fallbackTimeMs,
}: {
  currentTimeMs?: number;
  durationMs: number;
  fallbackTimeMs: number;
}) {
  return clampMs(currentTimeMs ?? fallbackTimeMs, 0, durationMs);
}

export function getNextAutoPlaybackFrame({
  currentFrame,
  deltaMs,
  durationMs,
  loop,
  loopResetMs,
}: AutoPlaybackFrameInput) {
  const normalizedDurationMs = clampMs(durationMs);
  const normalizedLoopResetMs = clampMs(loopResetMs);
  const elapsedMs = Math.max(0, deltaMs);

  if (normalizedDurationMs === 0) {
    return { ...currentFrame, timeMs: 0, resetRemainingMs: 0, shouldContinue: false };
  }

  if (currentFrame.resetRemainingMs > 0) {
    const resetRemainingMs = Math.max(0, currentFrame.resetRemainingMs - elapsedMs);

    if (resetRemainingMs > 0) {
      return { ...currentFrame, timeMs: CHAT_PLAYER_IDLE_TIME_MS, resetRemainingMs, shouldContinue: true };
    }

    return { ...currentFrame, timeMs: 0, resetRemainingMs: 0, shouldContinue: true };
  }

  const nextTimeMs = Math.max(0, currentFrame.timeMs) + elapsedMs;

  if (nextTimeMs < normalizedDurationMs) {
    return { ...currentFrame, timeMs: nextTimeMs, resetRemainingMs: 0, shouldContinue: true };
  }

  if (!loop) {
    return { ...currentFrame, timeMs: normalizedDurationMs, resetRemainingMs: 0, shouldContinue: false };
  }

  const playbackKey = currentFrame.playbackKey + 1;

  if (normalizedLoopResetMs > 0) {
    return {
      timeMs: CHAT_PLAYER_IDLE_TIME_MS,
      playbackKey,
      resetRemainingMs: normalizedLoopResetMs,
      shouldContinue: true,
    };
  }

  return {
    timeMs: nextTimeMs % normalizedDurationMs,
    playbackKey,
    resetRemainingMs: 0,
    shouldContinue: true,
  };
}

export function getNextAutoPlaybackDelayMs(chat: ChatPlayerCompiledChat, frame: ChatPlayerAutoPlaybackFrame) {
  if (frame.resetRemainingMs > 0) {
    return frame.resetRemainingMs;
  }

  if (frame.timeMs < 0) {
    return 0;
  }

  const nextUpdateTimeMs = getNextChatPlayerUpdateTimeMs(chat, frame.timeMs);

  if (nextUpdateTimeMs !== null) {
    return Math.max(0, nextUpdateTimeMs - frame.timeMs);
  }

  if (frame.timeMs < chat.durationMs) {
    return chat.durationMs - frame.timeMs;
  }

  return null;
}

export function getMediaPlaybackBoundaryDelayMs(
  chat: ChatPlayerCompiledChat,
  currentTimeMs: number,
  playbackRate: number,
) {
  const nextUpdateTimeMs = getNextChatPlayerUpdateTimeMs(chat, currentTimeMs);
  const normalizedPlaybackRate = Math.abs(playbackRate);

  if (nextUpdateTimeMs === null || normalizedPlaybackRate === 0) {
    return null;
  }

  return Math.max(0, (nextUpdateTimeMs - currentTimeMs) / normalizedPlaybackRate);
}

export function shouldSyncMediaPlaybackTime(chat: ChatPlayerCompiledChat, previousTimeMs: number, nextTimeMs: number) {
  if (previousTimeMs < 0 || nextTimeMs < previousTimeMs) {
    return true;
  }

  const nextUpdateTimeMs = getNextChatPlayerUpdateTimeMs(chat, previousTimeMs);

  if (nextUpdateTimeMs !== null) {
    return nextTimeMs >= nextUpdateTimeMs;
  }

  return previousTimeMs < chat.durationMs && nextTimeMs >= chat.durationMs;
}
